import React, { useState, useEffect } from "react";
import Linkify from "react-linkify";
import { db } from "../../firebase";
import styles from "./notificationBanner.module.scss";
import { useAuth } from "../../Context/AuthContext";

const NotificationBanner = () => {
  const { isAdmin, isSuperAdmin, isExecutive, isNcaa } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [paused, setPaused] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);

  // Snapshot listener...
  useEffect(() => {
    const unsubscribe = db
      .collection("notification-banner")
      .onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const now = new Date();
        const valid = data.filter((n) => {
          const expiry = n.expiresAt?.toDate?.() ?? new Date(n.expiresAt);
          return expiry > now;
        });
        const filtered = valid.filter((n) =>
          n.targetAccounts.includes("all") ||
          (isAdmin() && n.targetAccounts.includes("admin")) ||
          (isSuperAdmin() && n.targetAccounts.includes("super-admin")) ||
          (isExecutive() && n.targetAccounts.includes("executive")) ||
          (isNcaa() && n.targetAccounts.includes("ncaa"))
        );
        setNotifications(filtered);
      });
    return () => unsubscribe();
  }, [isAdmin, isSuperAdmin, isExecutive, isNcaa]);

  // Auto-rotate
  useEffect(() => {
    if (notifications.length > 1 && !paused) {
      const iv = setInterval(() => {
        setCurrentIndex((i) => (i + 1) % notifications.length);
      }, 7000);
      return () => clearInterval(iv);
    }
  }, [notifications, paused]);

  const handleDismiss = () => setVisible(false);
  const handleMouseEnter = () => setPaused(true);
  const handleMouseLeave = () => setPaused(false);

  if (!visible || notifications.length === 0) return null;

  const note = notifications[currentIndex];
  const fullMsg = note.message;
  const truncated = fullMsg.length > 200 ? fullMsg.slice(0, 200) + "…" : fullMsg;

  return (
    <>
      <div
        className={styles.notificationBanner}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button className={styles.dismiss} onClick={handleDismiss}>X</button>
        <div className={styles.notificationContent} key={note.id}>
          <h3 className={styles.title}>{note.title}</h3>
          <p className={styles.message}>
            <Linkify
              componentDecorator={(href, text, key) => (
                <a href={href} key={key} target="_blank" rel="noopener noreferrer">
                  {text}
                </a>
              )}
            >
              {truncated}
            </Linkify>
            {fullMsg.length > 200 && (
              <span
                className={styles.readMore}
                onClick={() => setShowReadMore(true)}
              >
                Read more
              </span>
            )}
          </p>
        </div>
        {notifications.length > 1 && (
          <div className={styles.dotContainer}>
            {notifications.map((_, i) => (
              <span
                key={i}
                className={`${styles.dot} ${i === currentIndex ? styles.active : ""}`}
                onClick={() => setCurrentIndex(i)}
              />
            ))}
          </div>
        )}
      </div>

      {showReadMore && (
        <div className={styles.readMoreModal}>
          <div className={styles.readMoreModalContent}>
            <h3>{note.title}</h3>
            <div className={styles.readMoreBody}>
              <p style={{ whiteSpace: "pre-wrap" }}>{fullMsg}</p>
            </div>
            <button onClick={() => setShowReadMore(false)}>Close</button>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationBanner;
